export const CART_BENEFITS_CONTRACT_VERSION = "cart-benefits-v2";
export const LEGACY_CART_BENEFITS_CONTRACT_VERSION = "cart-benefits-v1";
export const CART_BENEFITS_ALLOCATION_MODE = "proportional-eligible-value";
export const CART_BENEFITS_REDEMPTION_TAX_MODE = "pre-gst";
export const ECO_COIN_SELECTION_MODES = Object.freeze(["none", "maximum", "custom"]);

const normalizeText = (value = "") => String(value || "").trim();

export const normalizeCartCouponCode = (value = "") =>
  normalizeText(value)
    .toUpperCase()
    .replace(/[^A-Z0-9_-]/g, "")
    .slice(0, 40);

const toNonNegativeInteger = (value = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 0;
};

const toNonNegativeAmount = (value = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed * 100) / 100 : 0;
};

const toSignedAmount = (value = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.round(parsed * 100) / 100 : 0;
};

const normalizeTextList = (value = []) => {
  const values = Array.isArray(value) ? value : [value];
  return Array.from(
    new Set(
      values
        .flatMap((entry) =>
          Array.isArray(entry)
            ? entry
            : typeof entry === "string"
              ? entry.split(",")
              : [entry],
        )
        .map(normalizeText)
        .filter(Boolean),
    ),
  );
};

export const normalizeCartBenefitsRequest = (input = {}) => {
  const source = input?.benefits && typeof input.benefits === "object" ? input.benefits : input || {};
  const coupon = source.coupon && typeof source.coupon === "object" ? source.coupon : {};
  const coins = source.coins && typeof source.coins === "object" ? source.coins : {};
  const selection = source.selection && typeof source.selection === "object" ? source.selection : {};
  const coinSelection = selection.ecoCoins && typeof selection.ecoCoins === "object"
    ? selection.ecoCoins
    : selection.coins && typeof selection.coins === "object"
      ? selection.coins
      : {};
  const couponSelection = selection.coupon && typeof selection.coupon === "object" ? selection.coupon : {};
  const legacyUseAvailableCoins = Boolean(
    source.useAvailableCoins ||
      source.useAvailable ||
      source.redeemAll ||
      coins.useAvailableCoins ||
      coins.useAvailable ||
      coins.redeemAll ||
      coinSelection.useAvailableCoins ||
      coinSelection.useAvailable ||
      coinSelection.redeemAll,
  );
  const hasLegacySelectionOverride = [
    "useAvailableCoins",
    "useAvailable",
    "redeemAll",
    "requestedCoins",
    "redeemCoins",
    "coinsToRedeem",
  ].some((key) => Object.prototype.hasOwnProperty.call(source, key));
  // Explicit top-level compatibility fields represent the newest user action.
  // They must win over an older nested selection carried forward from the
  // previously applied cart benefits (for example maximum -> custom 250).
  const rawRequestedCoins = toNonNegativeInteger(
    hasLegacySelectionOverride
      ? source.requestedCoins ??
          source.redeemCoins ??
          source.coinsToRedeem ??
          coins.requestedCoins ??
          coins.selectedCoins ??
          coins.redeemCoins ??
          coins.coinsToRedeem ??
          coinSelection.requestedCoins ??
          coinSelection.selectedCoins ??
          coinSelection.redeemCoins
      : coinSelection.requestedCoins ??
          coinSelection.selectedCoins ??
          coinSelection.redeemCoins ??
          source.requestedCoins ??
          source.redeemCoins ??
          source.coinsToRedeem ??
          coins.requestedCoins ??
          coins.selectedCoins ??
          coins.redeemCoins ??
          coins.coinsToRedeem,
  );
  const rawMode = hasLegacySelectionOverride
    ? ""
    : normalizeText(
        coinSelection.mode || coinSelection.selectionMode || coins.selectionMode || coins.mode,
      ).toLowerCase();
  const selectionMode = ECO_COIN_SELECTION_MODES.includes(rawMode)
    ? rawMode
    : legacyUseAvailableCoins
      ? "maximum"
      : rawRequestedCoins > 0
        ? "custom"
        : "none";
  const useAvailableCoins = selectionMode === "maximum";
  const requestedCoins = selectionMode === "custom" ? rawRequestedCoins : 0;
  const hasLegacyCouponOverride = Object.prototype.hasOwnProperty.call(source, "couponCode");
  const couponCode = normalizeCartCouponCode(
    hasLegacyCouponOverride
      ? source.couponCode
      : couponSelection.code || coupon.code || coupon.couponCode,
  );

  return {
    version: CART_BENEFITS_CONTRACT_VERSION,
    couponCode,
    useAvailableCoins,
    requestedCoins,
    allocationMode: CART_BENEFITS_ALLOCATION_MODE,
    selection: {
      coupon: { code: couponCode },
      ecoCoins: { mode: selectionMode, requestedCoins },
    },
  };
};

export const buildCartBenefitsPayload = (input = {}) => ({
  benefits: normalizeCartBenefitsRequest(input),
});

export const buildCartPaymentBenefitsPayload = ({ benefits = null, fallbackRequest = {} } = {}) => {
  const authoritative = benefits?.contract?.authoritative === true;
  const quoteToken = authoritative ? normalizeText(benefits?.contract?.quoteToken) : "";
  return {
    benefitsQuoteToken: quoteToken,
    ...buildCartBenefitsPayload(benefits?.request || fallbackRequest),
  };
};

export const resolveAuthoritativeCartBenefitPayable = (benefits = null, fallback = null) => {
  const authoritative = benefits?.contract?.authoritative === true;
  const rawPayable = benefits?.totals?.payableAfterBenefits;
  const payable = rawPayable == null || rawPayable === "" ? NaN : Number(rawPayable);
  if (authoritative && Number.isFinite(payable) && payable >= 0) return payable;
  if (fallback === null || fallback === undefined || fallback === "") return null;
  const fallbackAmount = Number(fallback);
  return Number.isFinite(fallbackAmount) && fallbackAmount >= 0 ? fallbackAmount : null;
};

// Cart rows may still contain an earlier discounted price. Bind the latest
// benefit quote to the server quote IDs, not gross === current net payable.
export const cartBenefitQuoteMatchesItems = (benefits, items = []) => {
  if (benefits?.contract?.authoritative !== true || !benefits.contract.quoteToken || !items.length) return false;
  const rows = benefits.allocation;
  if (!Array.isArray(rows) || rows.length !== items.length) return false;
  const byCode = new Map(rows.map(row => [String(row.quoteCode || ""), row]));
  if (byCode.size !== rows.length || byCode.has("")) return false;
  if (new Set(items.map(item => String(item.quoteCode || item.quote?.quoteCode || ""))).size !== items.length) return false;
  const finiteAmount = value => value != null && value !== "" && Number.isFinite(Number(value)) && Number(value) >= 0;
  if (!items.every(item => {
    const code = String(item.quoteCode || item.quote?.quoteCode || "");
    const row = byCode.get(code);
    const id = item.serverId || item._id || item.id;
    return row && !item.stale && finiteAmount(row.payableAfter) && finiteAmount(row.payableBefore) &&
      (!row.cartItemId || !id || String(row.cartItemId) === String(id)) &&
      (!row.experienceType || !item.experienceType || row.experienceType === item.experienceType) &&
      (!item.serverTotals || (item.serverTotals.authoritative === true && item.serverTotals.status === "verified")) &&
      (!item.priceStatus || item.priceStatus === "verified");
  })) return false;
  return finiteAmount(benefits.totals?.payableAfterBenefits) && finiteAmount(benefits.totals?.grossPayable) &&
    Math.abs(rows.reduce((sum, row) => sum + Number(row.payableAfter), 0) - Number(benefits.totals.payableAfterBenefits)) < 0.005 &&
    Math.abs(rows.reduce((sum, row) => sum + Number(row.payableBefore), 0) - Number(benefits.totals.grossPayable)) < 0.005;
};

export const buildEmptyCartBenefitsSummary = (input = {}) => {
  const request = normalizeCartBenefitsRequest(input);
  return {
    version: CART_BENEFITS_CONTRACT_VERSION,
    mode: "cart",
    allocationMode: CART_BENEFITS_ALLOCATION_MODE,
    contract: {
      authoritative: false,
      quoteToken: "",
      quoteTokenVersion: "",
      currency: "INR",
      redemptionTaxMode: CART_BENEFITS_REDEMPTION_TAX_MODE,
      allocationMode: CART_BENEFITS_ALLOCATION_MODE,
    },
    request,
    coupon: {
      code: request.couponCode,
      status: request.couponCode ? "requested" : "none",
      discountAmount: 0,
    },
    coins: {
      useAvailableCoins: request.useAvailableCoins,
      requestedCoins: request.requestedCoins,
      selectionMode: request.selection.ecoCoins.mode,
      selectedCoins: 0,
      usedCoins: 0,
      walletCoins: 0,
      advanceCoins: 0,
      availableWalletCoins: 0,
      availableAdvanceCoins: 0,
      maximumUsableCoins: 0,
      defaultSelectedCoins: 0,
      defaultSelection: "maximum",
      editable: true,
      coinValue: 0,
      discountAmount: 0,
      preGstDiscountAmount: 0,
      payableSavings: 0,
      maximumDiscountAmount: 0,
      maximumPayableSavings: 0,
      holdExpiresAt: null,
      label: "Use 0 coins and save ₹0.00",
      selectedLabel: "Use 0 coins and save ₹0.00",
      defaultLabel: "Use 0 coins and save ₹0.00",
      status: request.useAvailableCoins || request.requestedCoins ? "requested" : "none",
      ledger: {
        holdLedgerId: "",
        holdLedgerIds: [],
        holdExpiresAt: null,
        confirmedLedgerId: "",
        confirmedLedgerIds: [],
        releasedLedgerId: "",
        releasedLedgerIds: [],
        advanceLedgerId: "",
        advanceLedgerIds: [],
      },
    },
    earn: {
      coins: 0,
      thresholdDiscount: 0,
      status: "none",
    },
    stacking: {
      version: "cart-benefit-stackability-v1",
      status: "not_applicable",
      requestedTogether: false,
      allowed: true,
      partiallyAllowed: false,
      coinSelectionAllowed: false,
      selectionEffect: "coupon_not_selected",
      blockingParty: "none",
      applicationScope: "eligible-items",
      couponAllows: true,
      ecoCoinPolicyAllows: true,
      coupon: {
        code: request.couponCode,
        applied: false,
        combinability: "none",
        allowsEcoCoins: true,
      },
      eligibleQuoteCodes: [],
      blockedQuoteCodes: [],
      eligibleCartItemIds: [],
      blockedCartItemIds: [],
      eligibleItemCount: 0,
      blockedItemCount: 0,
      applicableItemCount: 0,
      reasonCode: "",
      ruleText: "EcoCoin stacking is evaluated when a coupon is selected.",
      display: {
        tone: "neutral",
        title: "No coupon selected",
        badgeLabel: "EcoCoins available",
        message: "EcoCoin stacking is evaluated when a coupon is selected.",
        action: { type: "none", label: "", couponCode: "" },
      },
      evaluations: [],
    },
    totalDiscount: 0,
    totals: {
      grossPayable: 0,
      eligiblePreGstAmount: 0,
      couponDiscount: 0,
      ecoCoinPreGstDiscount: 0,
      rewardDiscount: 0,
      gstBefore: 0,
      gstAfter: 0,
      gstSavings: 0,
      totalBenefitAmount: 0,
      payableAfterBenefits: 0,
      payableSavings: 0,
    },
    calculation: {
      redemptionTaxMode: CART_BENEFITS_REDEMPTION_TAX_MODE,
      allocationMode: CART_BENEFITS_ALLOCATION_MODE,
      currency: "INR",
      amountPrecision: 2,
      coinPrecision: 0,
    },
    allocation: [],
    issues: [],
    messages: [],
  };
};

const sumAllocation = (rows = [], field = "") =>
  (Array.isArray(rows) ? rows : []).reduce((sum, row) => sum + toNonNegativeAmount(row?.[field]), 0);

const sumAllocationCoins = (rows = [], field = "") =>
  (Array.isArray(rows) ? rows : []).reduce((sum, row) => sum + toNonNegativeInteger(row?.[field]), 0);

export const buildCartBenefitTransactionLines = (benefits = {}) => {
  const hasSummaryShape = Boolean(
    benefits &&
      typeof benefits === "object" &&
      (benefits.coupon || benefits.coins || benefits.earn || benefits.allocation || benefits.totalDiscount),
  );
  const emptySummary = buildEmptyCartBenefitsSummary(benefits?.request || benefits || {});
  const legacyLedger = benefits?.coins?.ledger || {};
  const holdLedgerIds = normalizeTextList(
    legacyLedger.holdLedgerIds || benefits?.coins?.holdLedgerIds || legacyLedger.holdLedgerId || benefits?.coins?.holdLedgerId,
  );
  const confirmedLedgerIds = normalizeTextList(
    legacyLedger.confirmedLedgerIds ||
      benefits?.coins?.confirmedLedgerIds ||
      legacyLedger.confirmedLedgerId ||
      benefits?.coins?.confirmedLedgerId,
  );
  const releasedLedgerIds = normalizeTextList(
    legacyLedger.releasedLedgerIds ||
      benefits?.coins?.releasedLedgerIds ||
      legacyLedger.releasedLedgerId ||
      benefits?.coins?.releasedLedgerId,
  );
  const advanceLedgerIds = normalizeTextList(
    legacyLedger.advanceLedgerIds ||
      benefits?.coins?.advanceLedgerIds ||
      legacyLedger.advanceLedgerId ||
      benefits?.coins?.advanceLedgerId,
  );
  const holdExpiresAt = benefits?.coins?.holdExpiresAt || legacyLedger.holdExpiresAt || null;
  const summary =
    benefits && typeof benefits === "object" && benefits.version === CART_BENEFITS_CONTRACT_VERSION
      ? benefits
      : hasSummaryShape
        ? {
            ...emptySummary,
            ...benefits,
            coupon: {
              ...emptySummary.coupon,
              ...(benefits.coupon || {}),
            },
            coins: {
              ...emptySummary.coins,
              ...(benefits.coins || {}),
              ledger: {
                ...emptySummary.coins.ledger,
                ...(benefits.coins?.ledger || {}),
                holdLedgerId: holdLedgerIds[0] || "",
                holdLedgerIds,
                holdExpiresAt,
                confirmedLedgerId: confirmedLedgerIds[0] || "",
                confirmedLedgerIds,
                releasedLedgerId: releasedLedgerIds[0] || "",
                releasedLedgerIds,
                advanceLedgerId: advanceLedgerIds[0] || "",
                advanceLedgerIds,
              },
              holdExpiresAt,
            },
            earn: {
              ...emptySummary.earn,
              ...(benefits.earn || {}),
            },
            stacking: {
              ...emptySummary.stacking,
              ...(benefits.stacking || {}),
            },
            allocation: Array.isArray(benefits.allocation) ? benefits.allocation : [],
          }
        : emptySummary;
  const rows = Array.isArray(summary.allocation) ? summary.allocation : [];
  const coupon = summary.coupon || {};
  const coins = summary.coins || {};
  const earn = summary.earn || {};
  const ledger = coins.ledger || {};
  const couponDiscount = toNonNegativeAmount(coupon.discountAmount ?? sumAllocation(rows, "couponDiscount"));
  const coinDiscount = toNonNegativeAmount(coins.discountAmount ?? sumAllocation(rows, "coinDiscount"));
  const rewardDiscount = toNonNegativeAmount(
    earn.thresholdDiscount ?? earn.rewardDiscount ?? sumAllocation(rows, "rewardDiscount"),
  );
  const usedCoins = toNonNegativeInteger(coins.usedCoins ?? sumAllocationCoins(rows, "usedCoins"));
  const advanceCoins = toNonNegativeInteger(coins.advanceCoins ?? sumAllocationCoins(rows, "advanceCoins"));
  const earnCoins = toNonNegativeInteger(earn.coins ?? sumAllocationCoins(rows, "earnCoins"));
  const lines = [];

  if (couponDiscount > 0) {
    const code = normalizeCartCouponCode(coupon.code || summary.request?.couponCode);
    lines.push({
      id: "coupon-discount",
      kind: "coupon_discount",
      label: code ? `Coupon discount (${code})` : "Coupon discount",
      amount: toSignedAmount(-couponDiscount),
      coins: 0,
      status: normalizeText(coupon.status || "applied"),
      direction: "discount",
      referenceIds: code ? [code] : [],
      helper: "Applied across cart items",
    });
  }

  if (rewardDiscount > 0) {
    lines.push({
      id: "reward-threshold-discount",
      kind: "reward_threshold_discount",
      label: "Reward threshold discount",
      amount: toSignedAmount(-rewardDiscount),
      coins: 0,
      status: normalizeText(earn.status || "applied"),
      direction: "discount",
      referenceIds: [],
      helper: "Applied across cart items",
    });
  }

  if (coinDiscount > 0 || usedCoins > 0) {
    const references = normalizeTextList([
      ledger.confirmedLedgerIds,
      ledger.confirmedLedgerId,
      ledger.holdLedgerIds,
      ledger.holdLedgerId,
      ledger.advanceLedgerIds,
      ledger.advanceLedgerId,
      ledger.releasedLedgerIds,
      ledger.releasedLedgerId,
    ]);
    lines.push({
      id: "coin-redemption",
      kind: "coin_redemption",
      label: advanceCoins > 0 ? "Same-booking EcoCoin redemption" : "EcoCoin redemption (pre-GST)",
      amount: toSignedAmount(-coinDiscount),
      coins: usedCoins,
      status: normalizeText(coins.status || (usedCoins > 0 ? "applied" : "requested")),
      direction: "debit",
      referenceIds: references,
      helper:
        advanceCoins > 0
          ? `${advanceCoins} newly earned coins used immediately; applied before GST`
          : usedCoins > 0
            ? `${usedCoins} coins used; applied before GST`
            : "Coin usage requested",
    });
  }

  if (earnCoins > 0) {
    lines.push({
      id: "coin-earning",
      kind: "coin_earning",
      label: "EcoCoins earned",
      amount: 0,
      coins: earnCoins,
      status: normalizeText(earn.status || "quoted"),
      direction: "credit",
      referenceIds: [],
      helper: `${earnCoins} coins earned on this booking`,
    });
  }

  return lines;
};
